import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useEspecificacao } from '../../contexts/EspecificacaoContext';
import Folha from './Folha';
import Quantidade from './Quantidade';
import { agruparPorDepartamento } from '../../utils/especificacao';

/** Foto do item: produto do catálogo (proxy de imagem) ou foto enviada (item livre). */
export function FotoItem({ item, className = 'esp-item-foto' }) {
  const { apiUrl } = useAuth();
  const [erro, setErro] = useState(false);
  const src = item.foto ? (item.livre ? item.foto : `${apiUrl}${item.foto}`) : null;
  return (
    <div className={className}>
      {src && !erro ? <img src={src} alt="" loading="lazy" onError={() => setErro(true)} /> : <i className={`bi ${item.livre ? 'bi-lightbulb' : 'bi-box-seam'}`} />}
    </div>
  );
}

/**
 * Cesta lateral: a especificação ativa agrupada por departamento, com quantidade
 * editável na hora, troca de projeto e atalho pro item fora do catálogo.
 */
export default function CestaEspecificacao() {
  const navigate = useNavigate();
  const { fecharCesta, ativa, listaCatalogo: lista, escolher, criar, atualizarItem, removerItem, abrirLivre, carregandoAtiva } = useEspecificacao();
  const [novo, setNovo] = useState(null); // nome do projeto novo sendo digitado
  const [erro, setErro] = useState('');
  const grupos = agruparPorDepartamento(ativa?.itens);
  const total = ativa?.itens?.length || 0;

  const trocar = (v) => {
    if (v === 'novo') { setNovo(''); return; }
    setNovo(null);
    escolher(Number(v));
  };
  const criarNovo = async () => {
    if ((novo || '').trim().length < 2) return;
    try { await criar(novo.trim()); setNovo(null); setErro(''); }
    catch (e) { setErro(e.response?.data?.error || 'Não foi possível criar'); }
  };
  const irPara = (rota) => { fecharCesta(); navigate(rota); };

  const titulo = (
    <div>
      <div className="esp-cesta-rot"><i className="bi bi-clipboard2-check" /> Especificação</div>
      {lista.length > 0 && novo === null ? (
        <select className="esp-cesta-proj" value={ativa?.id || ''} onChange={e => trocar(e.target.value)} aria-label="Projeto">
          {!ativa && <option value="">Escolha um projeto…</option>}
          {lista.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
          <option value="novo">＋ Novo projeto…</option>
        </select>
      ) : (
        <div className="esp-cesta-novo">
          <input autoFocus value={novo || ''} maxLength={120} placeholder="Nome do projeto"
            onChange={e => setNovo(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') criarNovo(); }} />
          <button type="button" onClick={criarNovo} disabled={(novo || '').trim().length < 2}>Criar</button>
          {lista.length > 0 && <button type="button" className="sec" onClick={() => setNovo(null)}>Cancelar</button>}
        </div>
      )}
    </div>
  );

  return (
    <Folha
      lado
      titulo={titulo}
      subtitulo={ativa ? `${total} ${total === 1 ? 'item' : 'itens'}${ativa.cliente_nome ? ` · ${ativa.cliente_nome}` : ''}` : null}
      onFechar={fecharCesta}
      largura={440}
      rodape={ativa ? (
        <>
          <button type="button" className="esp-btn-sec" onClick={() => irPara('/especificacoes')}>Minhas especificações</button>
          <button type="button" className="esp-btn" onClick={() => irPara(`/especificacoes/${ativa.id}`)} disabled={!total}>
            Revisar e gerar PDF <i className="bi bi-arrow-right" />
          </button>
        </>
      ) : null}
    >
      {erro && <div className="esp-erro"><i className="bi bi-exclamation-circle" />{erro}</div>}
      {carregandoAtiva && !ativa && <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>}

      {!ativa && !carregandoAtiva && (
        <div className="esp-vazio">
          <i className="bi bi-clipboard2-plus" />
          <strong>Monte a lista do projeto do seu cliente</strong>
          <span>Dê um nome ao projeto acima e toque em <b>+ Especificar</b> nos produtos do catálogo. No fim, você gera o PDF quantitativo com a sua logo.</span>
        </div>
      )}

      {ativa && total === 0 && (
        <div className="esp-vazio">
          <i className="bi bi-bag-plus" />
          <strong>Nenhum item ainda</strong>
          <span>Navegue pelo catálogo e toque em <b>+ Especificar</b> nos produtos que vão entrar nesse projeto.</span>
          <button type="button" className="esp-btn-sec mt-2" onClick={() => irPara('/catalogo')}>Ir pro catálogo</button>
        </div>
      )}

      {grupos.map(g => (
        <div key={g.nome} className="esp-grupo">
          <div className="esp-grupo-tit">{g.nome}<span>{g.itens.length}</span></div>
          {g.itens.map(i => (
            <div key={i.id} className="esp-item">
              <FotoItem item={i} />
              <div className="esp-item-info">
                <div className="esp-item-nome" title={i.nome}>{i.nome}</div>
                <div className="esp-item-meta">
                  {[i.marca, i.medida].filter(Boolean).join(' · ')}
                  {i.livre && <span className="esp-tag-livre">fora do catálogo</span>}
                </div>
                {i.ambientes.length > 0 && <div className="esp-item-amb">Para: {i.ambientes.join(', ')}</div>}
                <div className="esp-item-acoes">
                  <Quantidade compacto valor={i.quantidade} unidade={i.unidade} onChange={q => atualizarItem(i.id, { quantidade: q })} />
                  {i.livre && <button type="button" className="esp-icone" onClick={() => abrirLivre(i)} aria-label="Editar item"><i className="bi bi-pencil" /></button>}
                  <button type="button" className="esp-icone perigo" onClick={() => removerItem(i.id)} aria-label="Remover item"><i className="bi bi-trash3" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}

      {ativa && (
        <button type="button" className="esp-livre-cta" onClick={() => abrirLivre()}>
          <i className="bi bi-lightbulb" />
          <span><strong>Não achou no catálogo?</strong> Descreva o item e a Centrão avalia se consegue trazer.</span>
          <i className="bi bi-chevron-right" />
        </button>
      )}
    </Folha>
  );
}
